"use client"

import styled from "@emotion/styled"
import { Box, Button } from "@mui/material"
import { useLocale } from "next-intl"
import Image from "next/image"
import { useCallback, useEffect, useRef, useState } from "react"
import { useInView } from "react-intersection-observer"
import type { Swiper as SwiperType } from "swiper"
import "swiper/css"
import "swiper/css/effect-fade"
import { Autoplay, EffectFade } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import { LeftArrow, RightArrow, StartIcon, StopSign, transitionTime, useIsDesktop } from "ui"
import {
  extractProperty,
  getLinkHrefFromMultiUrlPickerItem,
  getUmbracoMediaUrl,
  replaceRichTextLinks,
} from "utils"
import { useTypingAnimation } from "../hooks/useTypingAnimation"
import { useAnchorScroll } from "../useAnchorScroll"
import { LANDING_ELEMENTS, useLandingAnalytics } from "../analytics"

type Props = {
  data: any
}

// --- Styled Components ---

const Section = styled.section`
  display: flex;
  flex-direction: column;
  background-color: var(--comp-bg);
  color: var(--comp-text);
  position: relative;
  padding: 24px;
  gap: 16px;

  ${(props) => props.theme.breakpoints.up("md")} {
    flex-direction: row;
    padding: 64px 120px;
    gap: 45px;
  }

  ${(props) => props.theme.breakpoints.up("xl")} {
    padding: 64px 240px;
  }
`

const ContentArea = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  ${(props) => props.theme.breakpoints.up("md")} {
    flex: 1;
    gap: 24px;
  }
`

const LogoArea = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;

  img {
    width: 155px;
    height: 96px;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    flex-wrap: nowrap;
    gap: 24px;
    margin-bottom: 0;

    img {
      width: 157px;
      height: 97px;
    }
  }
`

const Headline = styled.h1`
  color: var(--comp-heading);
  margin: 0;
  font-size: 60px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: -1.2px;
  position: relative;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 72px;
    letter-spacing: 0;
    text-shadow: 0px 0px 25px #07101d;
  }
`

const HeadlinePlaceholder = styled.span`
  display: block;
  visibility: hidden;
`

const HeadlineTyped = styled.span`
  display: block;
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
`

const BodyText = styled.div`
  color: var(--comp-text);
  font-size: 18px;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: -0.36px;
  margin-top: 8px;
  margin-bottom: 16px;

  p {
    margin: 0;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 20px;
    letter-spacing: -0.4px;
    margin-top: 0;
    margin-bottom: 0;
  }
`

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;

  ${(props) => props.theme.breakpoints.up("md")} {
    max-width: 425px;
    gap: 24px;
  }
`

const ButtonsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  width: 100%;

  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 24px;
    width: auto;
  }
`

const CtaButton = styled(Button)`
  && {
    flex: 1;
    height: 48px;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 700;
    text-transform: none;
    background-color: var(--inner-bg);
    color: var(--inner-text);

    &:hover {
      background-color: var(--inner-bg);
      opacity: 0.9;
    }

    ${(props) => props.theme.breakpoints.up("md")} {
      flex: unset;
      font-size: 20px;
    }
  }
`

const SecondaryButton = styled(Button)`
  && {
    flex: 1;
    height: 48px;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 700;
    text-transform: none;
    background-color: transparent;
    color: var(--inner-bg);
    border: 2px solid var(--inner-bg);

    &:hover {
      background-color: transparent;
      opacity: 0.9;
    }

    ${(props) => props.theme.breakpoints.up("md")} {
      flex: unset;
      font-size: 20px;
    }
  }
`

const MediaAreaContainer = styled(Box)`
  position: relative;
  flex-shrink: 0;
  display: flex;
  min-height: max-content;
  flex-direction: column;
  justify-content: center;
`

const MediaArea = styled.div`
  position: relative;
  width: 100%;
  height: 295px;

  ${(props) => props.theme.breakpoints.up("md")} {
    width: 450px;
    height: 450px;
    aspect-ratio: unset;
    flex-shrink: 0;
    align-self: center;
  }

  ${(props) => props.theme.breakpoints.up("lg")} {
    width: 624px;
    height: 624px;
  }

  .swiper {
    width: 100%;
    height: 100%;
    padding-block: 0;
  }

  .swiper-slide {
    position: relative;
    width: 100%;
    height: 100%;
    opacity: 0 !important;
  }

  .swiper-slide-active {
    opacity: 1 !important;
  }
`

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 8px;

  ${(props) => props.theme.breakpoints.up("md")} {
    justify-content: flex-end;
    margin-top: 22px;
  }
`

const ArrowBtn = styled.button`
  background: var(--comp-bg, white);
  border: 1px solid var(--comp-text, black);
  border-radius: 50px;
  width: 32px;
  height: 32px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  svg {
    color: var(--comp-text);
    path {
      fill: var(--comp-text);
    }
  }

  &:hover:not(:disabled) {
    opacity: 0.8;
  }

  &:focus-visible {
    outline: 2px dashed var(--comp-text);
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`

const PlayPauseBtn = styled.button`
  background: white;
  border: none;
  border-radius: 40px;
  padding: 10px;
  opacity: 0.4;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;

  &:hover {
    opacity: 0.6;
  }

  &:focus-visible {
    outline: 2px dashed var(--comp-text);
    outline-offset: 2px;
  }
`

const VideoPlayPauseBtn = styled.button`
  position: absolute;
  bottom: 20px;
  inset-inline-end: 20px;
  z-index: 10;
  background: rgba(255, 255, 255, 0.4);
  border: none;
  border-radius: 40px;
  padding: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;

  &:hover {
    opacity: 0.7;
  }

  &:focus-visible {
    outline: 2px dashed white;
    outline-offset: 2px;
  }
`

const DotsContainer = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-top: 24px;

  ${(props) => props.theme.breakpoints.up("md")} {
    margin-top: 0;
    flex-direction: column;
    position: absolute;
    inset-inline-end: -24px;
    top: 50%;
    transform: translateY(-50%);
    width: auto;
  }
`

const Dot = styled.button<{ active: boolean }>`
  width: 8px;
  height: 8px;

  ${(props) => props.theme.breakpoints.up("md")} {
    width: 12px;
    height: 12px;
  }
  border-radius: 50%;
  border: none;
  cursor: pointer;
  padding: 0;
  background-color: ${({ active }) => (active ? "#414141" : "#CCCFD7")};
  transition: background-color 0.2s;

  &:focus-visible {
    outline: 2px dashed white;
    outline-offset: 2px;
  }
`

// --- Sub-components ---

function SingleMedia({ item }: { item: any }) {
  const analytics = useLandingAnalytics()
  const locale = useLocale()
  const isRtl = locale === "he" || !locale
  const url = item?.url || ""
  const isVideo = url.endsWith(".mp4") || url.endsWith(".webm")
  const videoRef = useRef<HTMLVideoElement>(null)
  const [paused, setPaused] = useState(false)

  if (isVideo) {
    return (
      <div style={{ position: "relative", width: "100%", height: "100%" }}>
        <video
          ref={videoRef}
          src={getUmbracoMediaUrl(url)}
          autoPlay
          muted
          loop
          playsInline
          onPlay={() => setPaused(false)}
          onPause={() => setPaused(true)}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
        <VideoPlayPauseBtn
          onClick={() => {
            if (videoRef.current) {
              analytics.mediaClick(paused ? "play" : "pause", LANDING_ELEMENTS.hero)
              if (paused) videoRef.current.play()
              else videoRef.current.pause()
            }
          }}
          aria-label={
            paused ? (isRtl ? "הפעל וידאו" : "Play video") : isRtl ? "עצור וידאו" : "Pause video"
          }
        >
          {paused ? <StartIcon /> : <StopSign />}
        </VideoPlayPauseBtn>
      </div>
    )
  }

  return (
    <Image
      src={getUmbracoMediaUrl(url)}
      fill
      alt={item?.altText || ""}
      style={{ objectFit: "cover" }}
    />
  )
}

// --- Main Component ---

function HeroOption4({ data }: Props) {
  const analytics = useLandingAnalytics()
  const locale = useLocale()
  const logos = extractProperty(data, "heroLogos", true) as any[] | undefined
  const headline = extractProperty(data, "heroHeadline") as string | undefined
  const bodyText = extractProperty(data, "heroBodyText") as string | undefined
  const ctaButton = extractProperty(data, "heroCtaButton") as any[] | undefined
  const secondaryBtn = extractProperty(data, "heroSecondaryButton") as any[] | undefined
  const desktopMedia = extractProperty(data, "heroBackgroundMedia", true) as any[] | undefined
  const mobileMedia = extractProperty(data, "heroBackgroundMediaMobile", true) as any[] | undefined
  const isDesktop = useIsDesktop()
  const mediaItems = isDesktop ? desktopMedia : mobileMedia?.length ? mobileMedia : desktopMedia

  const isRtl = locale === "he" || !locale
  const [swiperRef, setSwiperRef] = useState<SwiperType | null>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [isPlaying, setIsPlaying] = useState(true)
  const containerRef = useRef<HTMLDivElement>(null)
  const { ref: headlineRef, inView: headlineInView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  })
  const { displayed: typedHeadline, full: fullHeadline } = useTypingAnimation(
    headline,
    headlineInView,
  )

  const hasMedia = Array.isArray(mediaItems) && mediaItems.length > 0
  const hasMultipleMedia = hasMedia && mediaItems!.length > 1
  const showControls = hasMultipleMedia

  const togglePlayPause = useCallback(() => {
    if (swiperRef) {
      if (isPlaying) {
        swiperRef.autoplay.stop()
      } else {
        swiperRef.autoplay.start()
      }
    }
    analytics.mediaClick(isPlaying ? "pause" : "play", LANDING_ELEMENTS.hero)
    setIsPlaying(!isPlaying)
  }, [swiperRef, isPlaying, analytics])

  useEffect(() => {
    if (!containerRef.current || !hasMultipleMedia) return
    const slides = containerRef.current.querySelectorAll(".swiper-slide")
    slides.forEach((slide, i) => {
      if (i === activeIndex) {
        slide.removeAttribute("inert")
        slide.removeAttribute("aria-hidden")
      } else {
        slide.setAttribute("inert", "")
        slide.setAttribute("aria-hidden", "true")
      }
    })
  }, [activeIndex, hasMultipleMedia])

  const ctaLink = ctaButton?.[0]
  const secondaryLink = secondaryBtn?.[0]
  const ctaHref = ctaLink ? getLinkHrefFromMultiUrlPickerItem(ctaLink as any) : undefined
  const secondaryHref = secondaryLink
    ? getLinkHrefFromMultiUrlPickerItem(secondaryLink as any)
    : undefined
  const handleAnchorClick = useAnchorScroll()
  const logoItems = Array.isArray(logos) ? logos : []

  return (
    <Section>
      {/* Content — first in JSX = right side in RTL */}
      <ContentArea>
        {logoItems.length > 0 && (
          <LogoArea>
            {logoItems.map((logo: any, i: number) => (
              <Image
                key={i}
                src={getUmbracoMediaUrl(logo?.url || "/")}
                width={157}
                height={97}
                alt={logo?.altText || ""}
                style={{ objectFit: "contain" }}
              />
            ))}
          </LogoArea>
        )}

        <TextContainer>
          {headline && (
            <Headline ref={headlineRef}>
              <HeadlinePlaceholder>{fullHeadline}</HeadlinePlaceholder>
              <HeadlineTyped>{typedHeadline}</HeadlineTyped>
            </Headline>
          )}

          {bodyText && (
            <BodyText
              dangerouslySetInnerHTML={{
                __html: replaceRichTextLinks(bodyText, true),
              }}
            />
          )}

          {(ctaLink || secondaryLink) && (
            <ButtonsRow>
              {ctaLink && (
                <CtaButton
                  variant="contained"
                  href={ctaHref}
                  onClick={(e) => {
                    analytics.ctaClick(ctaLink.name, LANDING_ELEMENTS.hero)
                    handleAnchorClick(e, ctaHref)
                  }}
                  disableElevation
                >
                  {ctaLink.name}
                </CtaButton>
              )}
              {secondaryLink && (
                <SecondaryButton
                  variant="outlined"
                  href={secondaryHref}
                  onClick={(e) => {
                    analytics.ctaClick(secondaryLink.name, LANDING_ELEMENTS.hero)
                    handleAnchorClick(e, secondaryHref)
                  }}
                >
                  {secondaryLink.name}
                </SecondaryButton>
              )}
            </ButtonsRow>
          )}
        </TextContainer>
      </ContentArea>

      {/* Media — second in JSX = left side in RTL */}
      {hasMedia && (
        <MediaAreaContainer>
          <MediaArea ref={containerRef}>
            {mediaItems!.length === 1 ? (
              <SingleMedia item={mediaItems![0]} />
            ) : (
              <Swiper
                loop
                onSwiper={setSwiperRef}
                onSlideChange={(swiper) => {
                  setActiveIndex(swiper.realIndex)
                  const activeSlide = swiper.slides[swiper.activeIndex]
                  const video = activeSlide?.querySelector("video")
                  if (video) {
                    video.currentTime = 0
                    video.play()
                  }
                }}
                modules={[EffectFade, Autoplay]}
                effect="fade"
                speed={transitionTime * 1000}
                style={{ width: "100%", height: "100%" }}
                autoplay={
                  isPlaying
                    ? {
                        delay: 4500,
                        disableOnInteraction: false,
                      }
                    : false
                }
              >
                {mediaItems!.map((item: any, i: number) => (
                  <SwiperSlide key={i}>
                    <SingleMedia item={item} />
                  </SwiperSlide>
                ))}
              </Swiper>
            )}
          </MediaArea>

          {/* Controls below image */}
          {showControls && (
            <ControlsRow>
              <ArrowBtn
                onClick={() => swiperRef?.slidePrev()}
                aria-label={isRtl ? "לתמונה הקודמת" : "Previous slide"}
              >
                {isRtl ? <RightArrow /> : <LeftArrow />}
              </ArrowBtn>
              <PlayPauseBtn
                onClick={togglePlayPause}
                aria-label={
                  isPlaying
                    ? isRtl
                      ? "עצור תנועת הרכיב"
                      : "Stop animation"
                    : isRtl
                    ? "הפעל תנועה רכיב"
                    : "Play animation"
                }
              >
                {isPlaying ? <StopSign /> : <StartIcon />}
              </PlayPauseBtn>
              <ArrowBtn
                onClick={() => swiperRef?.slideNext()}
                aria-label={isRtl ? "לתמונה הבאה" : "Next slide"}
              >
                {isRtl ? <LeftArrow /> : <RightArrow />}
              </ArrowBtn>
            </ControlsRow>
          )}

          {/* Dots — horizontal on mobile, vertical on desktop (inside MediaArea) */}
          {hasMultipleMedia && (
            <DotsContainer>
              {mediaItems!.map((_: any, i: number) => (
                <Dot
                  key={i}
                  active={i === activeIndex}
                  onClick={() => swiperRef?.slideTo(i)}
                  aria-label={`${isRtl ? "שקופית" : "Slide"} ${i + 1}`}
                />
              ))}
            </DotsContainer>
          )}
        </MediaAreaContainer>
      )}
    </Section>
  )
}

export default HeroOption4
